import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { useLanguage } from "@/hooks/useLanguage";
import DomesticLoginPanel from "@/components/auth/DomesticLoginPanel";
import InternationalLoginPanel from "@/components/auth/InternationalLoginPanel";
import { Loader2 } from "lucide-react";

/**
 * 前台应用登录页面
 * Vercel 风格设计
 *
 * 根据系统运营区域配置显示不同的登录面板：
 * - 国内模式 (domestic): 显示手机号+微信登录，隐藏语言切换器，强制中文
 * - 海外模式 (international): 显示邮箱+Google+Apple登录，显示语言切换器
 */
export default function Login() {
  const { t } = useTranslation("login");
  const { isDomestic, loading } = useOperationRegion();
  const { changeLanguage } = useLanguage();
  const location = useLocation();

  // 获取重定向目标路径（优先从 state，其次从 URL 参数）
  const urlParams = new URLSearchParams(location.search);
  const fromParam = urlParams.get("from");
  const from = (location.state as any)?.from?.pathname || fromParam || "/";

  // 国内模式强制设置语言为中文
  useEffect(() => {
    if (isDomestic) {
      changeLanguage("zh");
    }
  }, [isDomestic, changeLanguage]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-[360px] flex flex-col -mt-16 sm:mt-0">
        {loading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/images/black-logo-notext.svg"
                alt="Tunzo"
                className="h-12 w-12 dark:hidden"
              />
              <img
                src="/images/white-logo-notext.svg"
                alt="Tunzo"
                className="h-12 w-12 hidden dark:block"
              />
            </div>

            {/* 标题区域 */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
                {t("title")}
              </h1>
              <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            </div>

            {/* 登录面板：根据运营区域显示对应面板 */}
            {isDomestic ? (
              <DomesticLoginPanel redirectTo={from} />
            ) : (
              <InternationalLoginPanel redirectTo={from} />
            )}

            {/* 注册链接 */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("common.noAccount")}{" "}
              <Link
                to={
                  from !== "/"
                    ? `/register?from=${encodeURIComponent(from)}`
                    : "/register"
                }
                className="text-foreground font-medium hover:text-primary transition-colors"
              >
                {t("common.register")}
              </Link>
            </p>
          </>
        )}
      </div>

      {/* 底部条款 */}
      <footer className="fixed bottom-0 left-0 right-0 py-6 px-4 text-center text-xs text-muted-foreground">
        <p>
          {t("app.loginAgreement", "继续即表示您同意我们的")}{" "}
          <Link to="/terms" className="hover:text-foreground transition-colors">
            {t("app.terms", "服务条款")}
          </Link>
          {t("app.agreementMiddle", " 和 ")}{" "}
          <Link
            to="/privacy"
            className="hover:text-foreground transition-colors"
          >
            {t("app.privacy", "隐私政策")}
          </Link>
        </p>
      </footer>
    </div>
  );
}
